import React, { useState } from "react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import type {
  AdminBlogLengthOption,
  AdminBlogTradeOption,
  AdminRouteMap,
} from "../types";

type AdminBlogNewPageProps = {
  adminRoutes: AdminRouteMap;
  csrfToken: string;
  tradeOptions: AdminBlogTradeOption[];
  lengthOptions: AdminBlogLengthOption[];
};

export const AdminBlogNewPage: React.FC<AdminBlogNewPageProps> = ({
  adminRoutes,
  csrfToken,
  tradeOptions,
  lengthOptions,
}) => {
  const [submittingGenerate, setSubmittingGenerate] = useState(false);
  const [submittingManual, setSubmittingManual] = useState(false);

  return (
    <DashboardPageLayout
      title="New blog post"
      subtitle="Generate a draft with AI based on your topic, or write one manually. All drafts must be reviewed before publishing."
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Generate with AI</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              method="post"
              action={adminRoutes.generateAdminBlogPost}
              onSubmit={() => setSubmittingGenerate(true)}
              className="space-y-4"
            >
              <input
                type="hidden"
                name="authenticity_token"
                value={csrfToken}
              />
              <div>
                <Label htmlFor="topic_or_title">Topic or working title</Label>
                <Input
                  id="topic_or_title"
                  name="blog_generation[topic_or_title]"
                  required
                  placeholder="e.g. How plumbers can rank for emergency calls"
                />
              </div>
              <div>
                <Label htmlFor="target_keyword">
                  Target keyword (optional)
                </Label>
                <Input
                  id="target_keyword"
                  name="blog_generation[target_keyword]"
                  placeholder="emergency plumber SEO"
                />
              </div>
              <div>
                <Label htmlFor="audience_trade">
                  Audience trade (optional)
                </Label>
                <select
                  id="audience_trade"
                  name="blog_generation[audience_trade]"
                  className="ds-native-select w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  defaultValue=""
                >
                  <option value="">(any)</option>
                  {tradeOptions.map((option) => (
                    <option key={option.slug} value={option.slug}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="desired_length">Desired length</Label>
                <select
                  id="desired_length"
                  name="blog_generation[desired_length]"
                  className="ds-native-select w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  defaultValue="medium"
                >
                  {lengthOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="outline_md">Outline (optional, markdown)</Label>
                <Textarea
                  id="outline_md"
                  name="blog_generation[outline_md]"
                  rows={6}
                  placeholder={
                    "## Why this matters\n## What to do\n## Common mistakes"
                  }
                />
              </div>
              <div>
                <Label htmlFor="voice">Voice (optional)</Label>
                <Input
                  id="voice"
                  name="blog_generation[voice]"
                  placeholder="ToolKit voice — direct, practical, no fluff"
                />
              </div>
              <Button
                type="submit"
                disabled={submittingGenerate}
                className="w-full"
              >
                {submittingGenerate ? "Generating…" : "Generate draft"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Or write manually</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              method="post"
              action={adminRoutes.adminBlogPostCreate}
              encType="multipart/form-data"
              onSubmit={() => setSubmittingManual(true)}
              className="space-y-4"
            >
              <input
                type="hidden"
                name="authenticity_token"
                value={csrfToken}
              />
              <div>
                <Label htmlFor="blog_post_title">Title</Label>
                <Input id="blog_post_title" name="blog_post[title]" required />
              </div>
              <div>
                <Label htmlFor="blog_post_meta_description">
                  Meta description
                </Label>
                <Input
                  id="blog_post_meta_description"
                  name="blog_post[meta_description]"
                  maxLength={320}
                />
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div>
                  <Label className="text-sm font-semibold">
                    Hero image (optional)
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    16:9 ratio recommended (1600×900). You can also add this
                    later when editing.
                  </p>
                </div>
                <Input
                  id="blog_post_hero_image"
                  name="blog_post[hero_image]"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                />
                <div>
                  <Label htmlFor="blog_post_hero_image_alt">
                    Hero image alt text
                  </Label>
                  <Input
                    id="blog_post_hero_image_alt"
                    name="blog_post[hero_image_alt]"
                    placeholder="Describe the image for accessibility & SEO"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="blog_post_excerpt_md">Excerpt (markdown)</Label>
                <Textarea
                  id="blog_post_excerpt_md"
                  name="blog_post[excerpt_md]"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="blog_post_body_md">Body (markdown)</Label>
                <Textarea
                  id="blog_post_body_md"
                  name="blog_post[body_md]"
                  rows={12}
                  required
                />
              </div>
              <div>
                <Label htmlFor="blog_post_target_keyword">Target keyword</Label>
                <Input
                  id="blog_post_target_keyword"
                  name="blog_post[target_keyword]"
                />
              </div>
              <div>
                <Label htmlFor="blog_post_trade_tags">
                  Trade tags (comma separated slugs)
                </Label>
                <Input
                  id="blog_post_trade_tags"
                  name="blog_post[trade_tags]"
                  placeholder="plumbers,electricians"
                />
              </div>
              <Button
                type="submit"
                disabled={submittingManual}
                className="w-full"
              >
                {submittingManual ? "Saving…" : "Save draft"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardPageLayout>
  );
};
